import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Paper,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import TranslationInput from '../../../components/PosMenu/TranslationInput';
import {
  fetchIngredients,
  createIngredient,
  updateIngredient,
  deleteIngredient,
} from '../../../redux/actions/posMenu';
import {
  DEFAULT_INGREDIENT,
  getTranslationValue,
} from '../../../constants/posMenu';
import { isPosProviderConnected } from '../../../utils/checkAccessDenied';

const PosMenuIngredients = () => {
  const dispatch = useDispatch();
  const { ingredients, loading } = useSelector((state) => state.posMenu);
  const {
    items: providerItems,
    loading: providersLoading,
    providerId,
  } = useSelector((state) => state.providers);
  const [open, setOpen] = useState(false);
  const [editingIngredient, setEditingIngredient] = useState(null);
  const [formData, setFormData] = useState(DEFAULT_INGREDIENT);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingIngredient, setDeletingIngredient] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Check if providers have been loaded (providerId is set when providers are fetched)
  const providersLoaded = providerId !== null || providerItems?.length > 0;

  // Check if POS is connected
  const isPosConnected = isPosProviderConnected(providerItems);

  useEffect(() => {
    dispatch(fetchIngredients());
  }, [dispatch]);

  const handleOpen = (ingredient = null) => {
    if (ingredient) {
      setEditingIngredient(ingredient);
      setFormData(ingredient);
    } else {
      setEditingIngredient(null);
      setFormData(DEFAULT_INGREDIENT);
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingIngredient(null);
    setFormData(DEFAULT_INGREDIENT);
  };

  const handleSave = async () => {
    if (!formData.name.en?.trim()) {
      toast.error('Name is Required');
      return;
    }

    setIsSaving(true);
    try {
      if (editingIngredient) {
        await dispatch(updateIngredient(editingIngredient._id, formData));
        toast.success('Ingredient updated successfully');
      } else {
        await dispatch(createIngredient(formData));
        toast.success('Ingredient created successfully');
      }
      handleClose();
      dispatch(fetchIngredients());
    } catch (error) {
      toast.error(error.message || 'An error occurred');
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenDeleteDialog = (ingredient) => {
    setDeletingIngredient(ingredient);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setDeletingIngredient(null);
  };

  const handleDelete = async () => {
    if (!deletingIngredient) return;

    setIsDeleting(true);
    try {
      await dispatch(deleteIngredient(deletingIngredient._id));
      toast.success('Ingredient deleted successfully');
      handleCloseDeleteDialog();
      dispatch(fetchIngredients());
    } catch (error) {
      toast.error(error.message || 'An error occurred');
    } finally {
      setIsDeleting(false);
    }
  };

  const filteredIngredients = ingredients.filter((ingredient) =>
    getTranslationValue(ingredient.name)
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  // Show loading while providers are loading or haven't been fetched yet
  if (providersLoading || !providersLoaded) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 'calc(100vh - 64px)',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Only show "POS Not Connected" after providers have finished loading
  if (!isPosConnected) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 'calc(100vh - 64px)',
          bgcolor: '#f5f5f5',
          p: 3,
        }}
      >
        <Paper
          sx={{
            p: 4,
            maxWidth: 500,
            textAlign: 'center',
          }}
        >
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            POS Not Connected
          </Typography>
          <Typography variant="body1" color="textSecondary" sx={{ mt: 2 }}>
            Please connect your business to a POS system to access the POS Menu
            Ingredients.
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            Go to <strong>Providers</strong> to connect your POS system.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">Ingredients Management</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpen()}
        >
          Add Ingredient
        </Button>
      </Box>

      <Card>
        <CardContent>
          <TextField
            label="Search Ingredients"
            variant="outlined"
            fullWidth
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
          />

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name (EN)</TableCell>
                    <TableCell>Name (ES)</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredIngredients.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        No ingredients found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredIngredients.map((ingredient) => (
                      <TableRow key={ingredient._id}>
                        <TableCell>
                          {getTranslationValue(ingredient.name, 'en')}
                        </TableCell>
                        <TableCell>
                          {getTranslationValue(ingredient.name, 'es')}
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            color="primary"
                            onClick={() => handleOpen(ingredient)}
                            size="small"
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            color="error"
                            onClick={() => handleOpenDeleteDialog(ingredient)}
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingIngredient ? 'Edit Ingredient' : 'Add Ingredient'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TranslationInput
              label="Name"
              value={formData.name}
              onChange={(value) => setFormData({ ...formData, name: value })}
              required
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={isSaving}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            color="primary"
            disabled={isSaving}
            startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete &quot;
            {getTranslationValue(deletingIngredient?.name) || 'this ingredient'}
            &quot;? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="primary" disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            autoFocus
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PosMenuIngredients;
